import type { CSSProperties, PropsWithChildren } from 'react';
import {
  readReportFieldSource,
  REPORT_MOCK_FIELD_OPACITY,
  type ReportFieldDataSource,
} from '@act/shared/data-transfer-objects';

export interface ReportMockFieldFrameProps extends PropsWithChildren {
  /** Section snapshot JSON (may include `fieldSources`). */
  data: Record<string, unknown>;
  /** Snapshot field key (e.g. `topNeeds`). */
  field: string;
  /** Optional explicit source override. */
  source?: ReportFieldDataSource;
  /** Optional className on the wrapper. */
  className?: string;
}

/**
 * Wraps a report chart block and fades it when the field uses illustrative mock data.
 * @param props - Snapshot field provenance and child content.
 */
export const ReportMockFieldFrame = ({
  data,
  field,
  source,
  className,
  children,
}: ReportMockFieldFrameProps) => {
  const resolvedSource = source ?? readReportFieldSource(data, field);
  const isMock = resolvedSource === 'mock';

  const style: CSSProperties = isMock
    ? { opacity: REPORT_MOCK_FIELD_OPACITY }
    : {};

  return (
    <div
      className={className}
      style={style}
      data-report-field={field}
      data-report-field-source={resolvedSource}
    >
      {children}
      {isMock ? (
        <p
          className="text-[10px] uppercase tracking-[0.12em] mt-3 mb-0"
          style={{ color: 'var(--midnight-75, #808E9B)', letterSpacing: '0.12em' }}
        >
          Illustrative data
        </p>
      ) : null}
    </div>
  );
};
